import { Download, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { BannerHero, Container } from "@/components/site/Layout";
import { SectionHead, ClaimBadge } from "@/components/site/Primitives";
import { NewsList } from "@/components/site/Modules";
import { NEWS, TIMELINE, EMAILS } from "@/lib/content";
import { useSeo } from "@/lib/useSeo";

const PRESS = TIMELINE.filter((t) => t.url);
const KIT = [
  ["AFR logo (full colour, horizontal)", "/img/afr-logo-horizontal.svg"],
  ["AFR logo (white)", "/img/afr-logo-white.svg"],
  ["AFR crest", "/img/afr-crest.svg"],
  ["Facility rendering (Mar 2026)", "/img/afr-site-2026-03.jpg"],
];

export default function NewsPage() {
  useSeo({ title: "News & Media | America First Refining", description: "Press releases, media coverage and press resources for America First Refining's Brownsville, Texas refinery project.", path: "/news-and-media" });
  return (
    <div data-testid="news-page">
      <BannerHero testId="news-banner" title="News & Media" image="/img/afr-site-2026-03.jpg" alt="Artist rendering of the AFR refinery at the Port of Brownsville" caption="Artist rendering · AFR" intro="Press releases, independent coverage and resources for journalists covering America First Refining." />
      <Container id="press" testId="press-section">
        <SectionHead eyebrow="Press releases" title="Latest announcements" />
        <ul className="border-t border-slate-200">
          {PRESS.map((p, i) => (
            <li key={p.url} className="border-b border-slate-200">
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`press-item-${i}`} className="group grid gap-3 py-6 transition-colors hover:bg-[#f3f5fa] md:grid-cols-12 md:items-center md:px-4">
                <span className="font-mono text-sm text-[#8a6420] md:col-span-2">{p.date}</span>
                <span className="font-display text-2xl font-bold uppercase leading-tight text-[#001870] md:col-span-7">{p.title}</span>
                <span className="text-sm md:col-span-3 md:text-right">{p.source} ↗</span>
              </a>
            </li>
          ))}
        </ul>
      </Container>
      <Container id="coverage" testId="coverage-section" tone="soft">
        <SectionHead eyebrow="Media coverage" title="In the news" body="Headlines are the outlets' own, linked at the source." />
        <NewsList items={NEWS} />
      </Container>
      <Container id="media-kit" testId="media-kit-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Media kit" title="Press resources" body="Brand files and imagery for editorial use. Executive headshots are on the leadership page." />
            <a href={`mailto:${EMAILS.press}`} data-testid="press-email-cta" className="inline-flex items-center gap-2 bg-[#001870] px-6 py-4 font-semibold text-white"><Mail className="h-4 w-4" />{EMAILS.press}</a>
            <p className="mt-4 text-sm"><Link to="/leadership" className="underline">View leadership headshots</Link></p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {KIT.map(([label, href], i) => (
              <li key={href}>
                <a href={href} download data-testid={`kit-item-${i}`} className="flex h-full items-center justify-between gap-4 border border-slate-200 p-5 transition-colors hover:border-[#001870]">
                  <span className="font-semibold text-[#001870]">{label}</span><Download className="h-5 w-5 shrink-0 text-[#8a6420]" />
                </a>
              </li>
            ))}
            <li className="sm:col-span-2"><ClaimBadge type="dated" testId="kit-badge" /> <span className="ml-2 text-sm">Replaces the Element Fuels logo files and policy PDF on the current media page.</span></li>
          </ul>
        </div>
      </Container>
    </div>
  );
}
